/**
 * SchoolGamification — Phase G
 * Tabs: Dashboard · Custom Badges · Teacher Leaderboard · Auto-Award · Weekly Brief
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Loader2, Trophy, Award, Plus, Trash2, Zap, Mail, Send, Sparkles,
  TrendingUp, Star, ShieldCheck, BookOpen
} from 'lucide-react';
import api from '@/lib/api';

const CRITERION_TYPES = [
  { value: 'attendance_streak', label: 'Attendance Streak (days)' },
  { value: 'attendance_percent', label: 'Attendance %' },
  { value: 'syllabus_percent', label: 'Syllabus %' },
  { value: 'exam_average', label: 'Exam Average %' },
  { value: 'engagement_score', label: 'Engagement Score' },
  { value: 'mentorship_count', label: 'Mentorship Count' },
  { value: 'composite_score', label: 'Composite Score (teachers)' },
];
const CATEGORIES = ['attendance', 'academics', 'engagement', 'mentorship', 'general'];
const AUDIENCES = [{ value: 'student', label: 'Students' }, { value: 'teacher', label: 'Teachers' }, { value: 'both', label: 'Both' }];

// ========== DASHBOARD ==========
const DashboardTab = ({ tenantId }) => {
  const { data: teachersLb = [] } = useQuery({ queryKey: ['g-teacher-lb', tenantId], queryFn: () => api.get(`/api/school/tenants/${tenantId}/gamification/teacher-leaderboard`).then(r => r.data) });
  const { data: studentsLb = {} } = useQuery({ queryKey: ['g-student-lb', tenantId], queryFn: () => api.get(`/api/school/tenants/${tenantId}/gamification/leaderboard?limit=10`).then(r => r.data) });
  const { data: systemBadges = { system_badges: [] } } = useQuery({ queryKey: ['g-teacher-sys', tenantId], queryFn: () => api.get(`/api/school/tenants/${tenantId}/gamification/teacher-badges-catalog`).then(r => r.data) });

  const studentLeaders = studentsLb.leaderboard || studentsLb.leaders || (Array.isArray(studentsLb) ? studentsLb : []);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card data-testid="g-teacher-leaderboard-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Trophy className="h-4 w-4 text-amber-500" /> Teacher Leaderboard</CardTitle></CardHeader>
        <CardContent>
          {teachersLb.length === 0 ? <p className="text-sm text-slate-500" data-testid="g-teacher-lb-empty">No teacher badges awarded yet. Run auto-award.</p> :
           <div className="space-y-1.5">
             {teachersLb.map((t, i) => (
               <div key={t.teacher_id} className="flex items-center justify-between p-2 rounded border bg-slate-50" data-testid={`g-teacher-lb-${i}`}>
                 <div className="flex items-center gap-2"><Badge variant="outline">#{i+1}</Badge><span className="text-sm font-medium">{t.name || '—'}</span></div>
                 <div className="flex gap-2"><Badge className="bg-amber-100 text-amber-800">{t.points} pts</Badge><Badge variant="secondary">{t.badges} badge{t.badges === 1 ? '' : 's'}</Badge></div>
               </div>
             ))}
           </div>}
        </CardContent>
      </Card>

      <Card data-testid="g-student-leaderboard-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Trophy className="h-4 w-4 text-indigo-500" /> Student Leaderboard</CardTitle></CardHeader>
        <CardContent>
          {(!studentLeaders || studentLeaders.length === 0) ? <p className="text-sm text-slate-500" data-testid="g-student-lb-empty">No student badges awarded yet.</p> :
           <div className="space-y-1.5">
             {studentLeaders.slice(0, 10).map((s, i) => (
               <div key={s.student_id || i} className="flex items-center justify-between p-2 rounded border bg-slate-50" data-testid={`g-student-lb-${i}`}>
                 <div className="flex items-center gap-2"><Badge variant="outline">#{i+1}</Badge><span className="text-sm font-medium">{s.name || s.student_name || '—'}</span></div>
                 <div className="flex gap-2"><Badge className="bg-indigo-100 text-indigo-800">{s.total_points ?? s.points ?? 0} pts</Badge><Badge variant="secondary">{s.badges_count ?? s.badges ?? 0}</Badge></div>
               </div>
             ))}
           </div>}
        </CardContent>
      </Card>

      <Card className="md:col-span-2" data-testid="g-teacher-catalog-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Award className="h-4 w-4 text-emerald-500" /> System Teacher Badges</CardTitle>
          <CardDescription>Automatically evaluated against live metrics during auto-award runs.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-3">
            {systemBadges.system_badges.map(b => (
              <div key={b.key} className="p-3 rounded-lg border bg-gradient-to-br from-indigo-50 to-emerald-50 flex items-center gap-3" data-testid={`g-sys-badge-${b.key}`}>
                <div className="text-3xl">{b.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{b.name}</div>
                  <div className="text-xs text-slate-600">{b.description}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{b.criterion_type} ≥ {b.criterion_value} · <b>{b.points} pts</b></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ========== CUSTOM BADGES ==========
const CustomBadgesTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', icon: '🏆', category: 'general', audience: 'student', criterion_type: 'attendance_percent', criterion_value: 80, points: 50, is_active: true });

  const { data = [], isLoading } = useQuery({ queryKey: ['g-custom', tenantId], queryFn: () => api.get(`/api/school/tenants/${tenantId}/gamification/custom-badges`).then(r => r.data) });

  const saveMut = useMutation({
    mutationFn: (body) => editId
      ? api.put(`/api/school/tenants/${tenantId}/gamification/custom-badges/${editId}`, body)
      : api.post(`/api/school/tenants/${tenantId}/gamification/custom-badges`, body),
    onSuccess: () => { toast.success('Saved'); setOpen(false); setEditId(null); qc.invalidateQueries({ queryKey: ['g-custom', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/api/school/tenants/${tenantId}/gamification/custom-badges/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['g-custom', tenantId] }); },
  });

  const openNew = () => { setEditId(null); setForm({ name: '', description: '', icon: '🏆', category: 'general', audience: 'student', criterion_type: 'attendance_percent', criterion_value: 80, points: 50, is_active: true }); setOpen(true); };
  const openEdit = (b) => { setEditId(b.id); setForm({ name: b.name, description: b.description, icon: b.icon, category: b.category, audience: b.audience, criterion_type: b.criterion_type, criterion_value: b.criterion_value, points: b.points, is_active: b.is_active }); setOpen(true); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Award className="h-4 w-4 text-purple-600" /> Custom Badges</CardTitle>
          <CardDescription>Define your own criteria. Awarded automatically when metrics reach the threshold.</CardDescription>
        </div>
        <Button onClick={openNew} data-testid="g-custom-new"><Plus className="h-4 w-4 mr-1" /> New Badge</Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
         data.length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="g-custom-empty">No custom badges yet.</p> :
         <Table>
          <TableHeader><TableRow><TableHead>Icon</TableHead><TableHead>Name</TableHead><TableHead>Audience</TableHead><TableHead>Criterion</TableHead><TableHead>Points</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.map(b => (
              <TableRow key={b.id} data-testid={`g-custom-row-${b.id}`}>
                <TableCell className="text-2xl">{b.icon}</TableCell>
                <TableCell className="font-medium">{b.name}<div className="text-xs text-slate-500">{b.description}</div></TableCell>
                <TableCell><Badge variant="outline">{b.audience}</Badge></TableCell>
                <TableCell className="text-xs">{b.criterion_type} ≥ {b.criterion_value}</TableCell>
                <TableCell><Badge className="bg-amber-100 text-amber-800">{b.points}</Badge></TableCell>
                <TableCell>{b.is_active ? '✓' : '—'}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`g-custom-edit-${b.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('Delete badge?')) delMut.mutate(b.id); }} data-testid={`g-custom-del-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
         </Table>}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'New'} Custom Badge</DialogTitle><DialogDescription>Criteria are evaluated during each auto-award run.</DialogDescription></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="g-custom-name" /></div>
            <div><Label>Icon (emoji)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-20 text-2xl" data-testid="g-custom-icon" /></div>
            <div><Label>Points</Label><Input type="number" value={form.points} onChange={(e) => setForm({ ...form, points: Number(e.target.value) })} data-testid="g-custom-points" /></div>
            <div><Label>Audience</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="g-custom-audience"><SelectValue /></SelectTrigger>
                <SelectContent>{AUDIENCES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="g-custom-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Criterion type</Label>
              <Select value={form.criterion_type} onValueChange={(v) => setForm({ ...form, criterion_type: v })}>
                <SelectTrigger data-testid="g-custom-crit-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CRITERION_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Criterion value (≥)</Label><Input type="number" value={form.criterion_value} onChange={(e) => setForm({ ...form, criterion_value: Number(e.target.value) })} data-testid="g-custom-crit-value" /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="g-custom-desc" /></div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="g-custom-active" /> Active (evaluated during auto-award)</label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(form)} disabled={!form.name || saveMut.isPending} data-testid="g-custom-save">
              {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ========== AUTO-AWARD ==========
const AutoAwardTab = ({ tenantId }) => {
  const [scope, setScope] = useState('both');
  const [result, setResult] = useState(null);
  const awardMut = useMutation({
    mutationFn: () => api.post(`/api/school/tenants/${tenantId}/gamification/auto-award?scope=${scope}`),
    onSuccess: (r) => { setResult(r.data); toast.success(`${r.data.new_badges} new badge(s) awarded`); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Zap className="h-4 w-4 text-amber-600" /> Auto-Award Engine</CardTitle>
        <CardDescription>Scans every active student and teacher, evaluates each badge's criterion against live metrics, and awards every new qualifying badge. Safe to run repeatedly.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-3">
          <div><Label>Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-40" data-testid="g-auto-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="both">Students + Teachers</SelectItem><SelectItem value="student">Students only</SelectItem><SelectItem value="teacher">Teachers only</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={() => awardMut.mutate()} disabled={awardMut.isPending} data-testid="g-auto-run">
            {awardMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />} Run Auto-Award
          </Button>
        </div>
        {result && (
          <div className="grid md:grid-cols-3 gap-3 pt-2">
            <div className="p-3 rounded-lg border bg-emerald-50"><div className="text-xs text-emerald-700">Students awarded</div><div className="text-2xl font-bold text-emerald-900" data-testid="g-auto-students">{result.awarded_students}</div></div>
            <div className="p-3 rounded-lg border bg-indigo-50"><div className="text-xs text-indigo-700">Teachers awarded</div><div className="text-2xl font-bold text-indigo-900" data-testid="g-auto-teachers">{result.awarded_teachers}</div></div>
            <div className="p-3 rounded-lg border bg-amber-50"><div className="text-xs text-amber-700">Total new badges</div><div className="text-2xl font-bold text-amber-900" data-testid="g-auto-total">{result.new_badges}</div></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ========== WEEKLY BRIEF ==========
const WeeklyBriefTab = ({ tenantId }) => {
  const [recipients, setRecipients] = useState('');
  const { data: preview, isLoading, refetch } = useQuery({
    queryKey: ['g-weekly-brief', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/gamification/weekly-brief/preview`).then(r => r.data),
  });
  const sendMut = useMutation({
    mutationFn: (body) => api.post(`/api/school/tenants/${tenantId}/gamification/weekly-brief/send`, body),
    onSuccess: (r) => toast.success(`${r.data.queued} queued · ${r.data.direct_sent_with_pdf} direct-sent with PDF`),
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const d = preview || {};
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-indigo-600" /> Monday Executive Brief</CardTitle>
          <CardDescription>Snapshot + week-on-week risk delta + AI commentary. Attaches the Executive Dashboard PDF.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="g-weekly-refresh">Refresh</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            <div className="p-3 rounded-lg border bg-gradient-to-br from-indigo-50 to-white" data-testid="g-weekly-commentary">
              <div className="text-xs font-medium text-indigo-900 mb-1">💡 AI commentary</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{d.commentary || 'Insufficient data yet.'}</div>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <div className="p-3 rounded border bg-rose-50"><div className="text-xs text-rose-700">At-risk this week</div><div className="text-2xl font-bold text-rose-900" data-testid="g-weekly-atrisk">{d.at_risk_count || 0}</div></div>
              <div className="p-3 rounded border bg-slate-50"><div className="text-xs">Last week</div><div className="text-2xl font-bold text-slate-900">{d.prev_at_risk_count || 0}</div></div>
              <div className="p-3 rounded border bg-amber-50"><div className="text-xs text-amber-700">New at-risk</div><div className="text-2xl font-bold text-amber-900" data-testid="g-weekly-new">{(d.new_at_risk_ids || []).length}</div></div>
              <div className="p-3 rounded border bg-emerald-50"><div className="text-xs text-emerald-700">Recovered</div><div className="text-2xl font-bold text-emerald-900" data-testid="g-weekly-recovered">{(d.recovered_ids || []).length}</div></div>
            </div>
            <div>
              <Label>Override recipients (optional)</Label>
              <Input placeholder="principal@school.com, hr@school.com" value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="g-weekly-recipients" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => sendMut.mutate({ recipients: recipients.split(',').map(s => s.trim()).filter(Boolean), dry_run: true, attach_pdf: true })} data-testid="g-weekly-preview-btn">
                <Mail className="h-4 w-4 mr-1" /> Dry-run
              </Button>
              <Button onClick={() => sendMut.mutate({ recipients: recipients.split(',').map(s => s.trim()).filter(Boolean), dry_run: false, attach_pdf: true })} disabled={sendMut.isPending} data-testid="g-weekly-send-btn">
                {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send Brief with PDF
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ========== MAIN ==========
export const SchoolGamification = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="gamification-panel">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2"><Trophy className="h-5 w-5" /> Gamification</h2>
        <p className="text-sm text-slate-500">Badges · Leaderboards · Auto-Award · Monday Executive Brief</p>
      </div>
      <Tabs defaultValue="dashboard">
        <TabsList className="flex-wrap">
          <TabsTrigger value="dashboard" data-testid="g-tab-dashboard"><Trophy className="h-4 w-4 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="custom" data-testid="g-tab-custom"><Award className="h-4 w-4 mr-1" /> Custom Badges</TabsTrigger>
          <TabsTrigger value="auto" data-testid="g-tab-auto"><Zap className="h-4 w-4 mr-1" /> Auto-Award</TabsTrigger>
          <TabsTrigger value="brief" data-testid="g-tab-brief"><Sparkles className="h-4 w-4 mr-1" /> Weekly Brief</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><DashboardTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="custom" className="mt-4"><CustomBadgesTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="auto" className="mt-4"><AutoAwardTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="brief" className="mt-4"><WeeklyBriefTab tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolGamification;
